/**
 * Tracks whether the user is currently driving the page with the keyboard or
 * with a pointer / touch. Lets focus-management code move focus for keyboard
 * and assistive-tech users (who need a landing spot after a view change)
 * without doing it for touch users, where a script-moved focus draws a focus
 * ring on whatever received it.
 */
export type InputModality = 'keyboard' | 'pointer' | 'unknown';

let modality: InputModality = 'unknown';
let installed = false;

const markKeyboard = (event: KeyboardEvent) => {
  // Modifier chords (copy, tab switching) say nothing about how the page is
  // being navigated.
  if (event.metaKey || event.ctrlKey || event.altKey) return;
  modality = 'keyboard';
};
const markPointer = () => {
  modality = 'pointer';
};

/** Start listening. Idempotent; safe to call from any component's mount effect. */
export function installInputModalityTracker(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener('keydown', markKeyboard, true);
  document.addEventListener('pointerdown', markPointer, true);
  document.addEventListener('mousedown', markPointer, true);
  document.addEventListener('touchstart', markPointer, { capture: true, passive: true });
}

export function getInputModality(): InputModality {
  return modality;
}
